/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    PROFILE_TAB_COMP
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Extracted profile tab content for ReviewProfileForm.
 * Uses shared icon-based ReviewUserContact component for design consistency.
 *
 * File Size Check:
 * - Current: ~130 lines
 * - Limit: 200 lines
 * - Decision: New component creation (under limit)
 *
 * Related Screens / Modules:
 * - client/src/components/setup/ReviewProfileForm.tsx
 */
import React from "react";
import { Row, Col, Card, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import type { User, ImageData } from "../../../../interfaces/User";
import BroadcastSignatureEditor, {
  type BroadcastSignatureEditorRef,
} from "../../broadcast-signature-editor/BroadcastSignatureEditor";
import { ReviewUserContact } from "../../review-common/components/ReviewUserContact";

interface ProfileTabProps {
  profile: Partial<User> | null;
  getImageSrc: (img?: ImageData | null) => string | null;
  broadcastSignatureHtml: string;
  isDirty: boolean;
  isSaving: boolean;
  saved: boolean;
  onSignatureChange: (html: string) => void;
  onSaveSignature: () => void;
  signatureEditorRef: React.RefObject<BroadcastSignatureEditorRef>;
}

export const ProfileTab: React.FC<ProfileTabProps> = ({
  profile,
  getImageSrc,
  broadcastSignatureHtml,
  isDirty,
  isSaving,
  saved,
  onSignatureChange,
  onSaveSignature,
  signatureEditorRef,
}) => {
  const navigate = useNavigate();
  const profileImage = (profile as any)?.profileImages as ImageData | undefined;

  const handleEdit = () => {
    navigate("/setup?section=profile");
  };

  return (
    <Row className="review-section" style={{ minWidth: 0 }}>
      <Col md={8} style={{ minWidth: 0 }}>
        <Card className="review-card mb-3" style={{ width: "100%", maxWidth: "100%" }}>
          <Card.Body>
            <div className="review-page-header d-flex justify-content-end mb-4">
              <Button
                variant="outline-secondary"
                className="review-edit-button"
                onClick={handleEdit}
                aria-label="Edit profile"
              >
                <img src="/assets/editprofile.svg" alt="Edit" width={32} height={32} />
              </Button>
            </div>

            <div className="d-flex align-items-start justify-content-between mb-3">
              <div className="d-flex align-items-center gap-3">
                {profileImage && (
                  <div className="review-avatar">
                    <img src={getImageSrc(profileImage) || ""} alt="Profile" />
                  </div>
                )}
                <div>
                  <h4 className="mb-1">
                    {profile?.firstName || profile?.lastName
                      ? `${profile?.firstName || ""} ${profile?.lastName || ""}`.trim()
                      : "Your name"}
                  </h4>
                  <div className="text-muted">
                    {profile?.jobTitle || "Your position"}, {profile?.companyName || ""}
                  </div>
                </div>
              </div>
            </div>

            <div className="review-section-block">
              <h6 className="review-section-title">
                About {profile?.firstName || "me"}
              </h6>
              <p className="review-section-text">
                {profile?.bio ||
                  "Tell others a bit about yourself, your role and your experience."}
              </p>
            </div>
          </Card.Body>
        </Card>

        <Card className="review-card" style={{ width: "100%", maxWidth: "100%" }}>
          <Card.Header className="d-flex align-items-center justify-content-between">
            <div>
              <h6 className="mb-0">My Broadcast Signature</h6>
              <div className="text-muted u-P3-14">
                (will be presented in your broadcast email only)
              </div>
            </div>
            <Button
              className="btn-black btn-sm"
              disabled={!isDirty || isSaving}
              onClick={onSaveSignature}
            >
              {isSaving ? "Saving..." : saved ? "Saved ✓" : "Save Signature"}
            </Button>
          </Card.Header>
          <Card.Body>
            <BroadcastSignatureEditor
              ref={signatureEditorRef}
              value={broadcastSignatureHtml}
              onChange={onSignatureChange}
            />
          </Card.Body>
        </Card>
      </Col>

      <Col md={4} style={{ minWidth: 0 }}>
        {/* [2025-12-29] Icon-based contact info list matching Figma */}
        <ReviewUserContact profile={profile} />

        <Card className="review-card" style={{ width: "100%", maxWidth: "100%" }}>
          <Card.Body>
            <h6 className="mb-3">Social media</h6>
            <div className="review-social-list">
              {[
                { label: "LinkedIn", value: profile?.linkedIn },
                { label: "Facebook", value: profile?.facebook },
                { label: "X / Twitter", value: profile?.x },
                { label: "Instagram", value: profile?.instagram },
              ].map((item, idx) => (
                <div key={idx} className="review-social-row">
                  <span className="review-social-label">{item.label}</span>
                  <span className="review-social-value">
                    {item.value ? (
                      <a href={item.value} target="_blank" rel="noopener noreferrer">
                        {item.value}
                      </a>
                    ) : (
                      "Not provided"
                    )}
                  </span>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};
